import type { ManagerialDashboardClientProfitability } from '@/types/api'
import {
  formatFullCurrencyBrl,
  formatPercentDisplay,
  resolveMarginPercent,
} from './managerial-kpis'

export type ClientProfitabilityTone = 'amber' | 'ink' | 'orange' | 'slate' | 'teal'

export type ClientProfitability = {
  id: string
  name: string
  icon: string
  tone: ClientProfitabilityTone
  revenue: number
  costs: number
  profit: number
}

export type ClientProfitabilityView = {
  id: string
  name: string
  icon: string
  tone: ClientProfitabilityTone
  formattedRevenue: string
  formattedCosts: string
  formattedProfit: string
  formattedMargin: string
  marginBarPercent: number
}

const MARGIN_BAR_VISUAL_MAX = 40

const CLIENT_PROFITABILITY_TONES: ClientProfitabilityTone[] = [
  'amber',
  'ink',
  'orange',
  'slate',
  'teal',
]

export const CLIENT_PROFITABILITY_ICON = 'mdi:domain'

export const EMPTY_CLIENT_PROFITABILITY_VIEWS: ClientProfitabilityView[] = []

export function resolveMarginBarPercent(margin: number): number {
  return Math.min(100, Math.max(0, (margin / MARGIN_BAR_VISUAL_MAX) * 100))
}

export function toClientProfitabilityView(
  client: ClientProfitability
): ClientProfitabilityView {
  const margin = resolveMarginPercent(client.revenue, client.profit)

  return {
    id: client.id,
    name: client.name,
    icon: client.icon,
    tone: client.tone,
    formattedRevenue: formatFullCurrencyBrl(client.revenue),
    formattedCosts: formatFullCurrencyBrl(client.costs),
    formattedProfit: formatFullCurrencyBrl(client.profit),
    formattedMargin: formatPercentDisplay(margin),
    marginBarPercent: resolveMarginBarPercent(margin),
  }
}

export function mapClientProfitability(
  rows: ManagerialDashboardClientProfitability[]
): ClientProfitabilityView[] {
  return rows.map((row, index) =>
    toClientProfitabilityView({
      id: String(row.id),
      name: row.name,
      icon: CLIENT_PROFITABILITY_ICON,
      tone:
        CLIENT_PROFITABILITY_TONES[index % CLIENT_PROFITABILITY_TONES.length] ?? 'teal',
      revenue: row.revenue,
      costs: row.costs,
      profit: row.profit,
    })
  )
}

/** Fixture de formatação da tabela (não vai para a página). */
export const PLACEHOLDER_CLIENT_PROFITABILITY: ClientProfitability[] = [
  {
    id: 'instituto-onca-pintada',
    name: 'Instituto Onça Pintada',
    icon: 'mdi:paw',
    tone: 'amber',
    revenue: 642_880,
    costs: 412_300,
    profit: 230_580,
  },
  {
    id: 'instituto-luisa-mell',
    name: 'Instituto Luisa Mell',
    icon: 'mdi:paw',
    tone: 'ink',
    revenue: 428_450,
    costs: 301_720,
    profit: 126_730,
  },
  {
    id: 'supermercados-ipanema',
    name: 'Supermercados Ipanema',
    icon: 'mdi:cart-outline',
    tone: 'orange',
    revenue: 312_600,
    costs: 227_410,
    profit: 85_190,
  },
  {
    id: 'leo-racoes',
    name: 'Leo Rações',
    icon: 'mdi:dog',
    tone: 'slate',
    revenue: 298_320,
    costs: 206_180,
    profit: 92_140,
  },
  {
    id: 'cliente-a-confidencial',
    name: 'Cliente A (Confidencial)',
    icon: 'mdi:account-lock-outline',
    tone: 'teal',
    revenue: 241_500,
    costs: 187_360,
    profit: 54_140,
  },
]

export function getPlaceholderClientProfitabilityViews(): ClientProfitabilityView[] {
  return PLACEHOLDER_CLIENT_PROFITABILITY.map(toClientProfitabilityView)
}
